/**
 * Area (district) landing pages: /area/<slug>.
 *
 * Why these exist: the busiest searches in this market are "<area> off plan"
 * and "مشاريع <area> على الخارطة" — and until now the only page that could
 * answer them was /projects?district=..., a filtered query that is neither
 * prerendered nor canonical. Each area here gets a real, crawlable page whose
 * numbers (project count, starting price, developers, handover years) are
 * computed from the live feed at build time, so the copy is unique per area
 * and never goes stale.
 *
 * Like projectSeo.js this module is free of React and browser APIs: the
 * prerenderer and the React page both import it, so the HTML Google fetches
 * and the DOM it renders say the same thing.
 *
 * `match`  — district names exactly as the REM feed spells them. Several
 *            areas are split across more than one feed name.
 * `query`  — the substring sent to the API's district_name filter from the
 *            browser; the response is then narrowed to `match`.
 * `short`  — the Latin name people actually type, even in Arabic searches
 *            ("JVC", "Business Bay"), so it goes into the Arabic title too.
 */

import { normaliseProject } from './projectSeo.js';
import { compactPrice, fullPrice, projectCount, listJoin, cityName, districtLabel, slugify } from './seoCopy.js';

export const AREAS = [
    {
        slug: 'jvc',
        short: 'JVC',
        ar: 'قرية جميرا الدائرية',
        en: 'Jumeirah Village Circle',
        city: 'Dubai',
        match: ['JVC (Jumeirah Village Circle)'],
        query: 'JVC',
        introAr: 'قرية جميرا الدائرية (JVC) أكثر مناطق دبي طرحاً للمشاريع على الخارطة، وتقع بين شارع الشيخ محمد بن زايد وشارع الخيل. تجذب المستثمرين لأن أسعار الدخول فيها أقل من المناطق المركزية مع طلب إيجاري مرتفع من العائلات والموظفين، وأغلب وحداتها استوديو وشقق غرفة وغرفتين.',
        introEn: 'Jumeirah Village Circle (JVC) has more off-plan launches than any other Dubai community, set between Sheikh Mohammed Bin Zayed Road and Al Khail Road. Investors favour it for entry prices well below central Dubai and steady rental demand from families and professionals; most stock is studios, one- and two-bedroom apartments.'
    },
    {
        slug: 'business-bay',
        short: 'Business Bay',
        ar: 'الخليج التجاري (بزنس باي)',
        en: 'Business Bay',
        city: 'Dubai',
        match: ['Business Bay'],
        query: 'Business Bay',
        introAr: 'الخليج التجاري (بزنس باي) حيّ أبراج على قناة دبي المائية بجوار وسط مدينة دبي وبرج خليفة. يجمع بين الشقق السكنية والشقق الفندقية والمكاتب، ويستهدف من يريد موقعاً مركزياً بإطلالات على القناة أو البرج وطلباً قوياً على الإيجار القصير.',
        introEn: 'Business Bay is a high-rise district on the Dubai Water Canal, next to Downtown Dubai and Burj Khalifa. It mixes residential towers, serviced apartments and offices, and suits buyers who want a central address with canal or Burj views and strong short-let demand.'
    },
    {
        slug: 'dubai-south',
        short: 'Dubai South',
        ar: 'دبي الجنوب',
        en: 'Dubai South',
        city: 'Dubai',
        match: ['Dubai South'],
        query: 'Dubai South',
        introAr: 'دبي الجنوب مدينة متكاملة حول مطار آل مكتوم الدولي الذي يجري توسيعه ليصبح أكبر مطارات المنطقة، وقرب موقع إكسبو. تضم مشاريع شقق وتاون هاوس وفلل بأسعار دخول منخفضة نسبياً، ويراهن المستثمرون فيها على نمو الطلب مع اكتمال المطار.',
        introEn: 'Dubai South is a master-planned city built around Al Maktoum International Airport, which is being expanded into the region\'s largest, and next to Expo City. It offers apartments, townhouses and villas at comparatively low entry prices, and buyers here are betting on demand growing as the airport comes online.'
    },
    {
        slug: 'dubai-islands',
        short: 'Dubai Islands',
        ar: 'جزر دبي',
        en: 'Dubai Islands',
        city: 'Dubai',
        match: ['Dubai Islands'],
        query: 'Dubai Islands',
        introAr: 'جزر دبي (جزر ديرة سابقاً) مجموعة جزر على ساحل ديرة تطوّرها نخيل، وتضم شواطئ ومرافئ ومنتجعات. أصبحت من أنشط مناطق الإطلاقات الجديدة على الخارطة، وتستهدف من يبحث عن سكن على البحر بأسعار أقل من نخلة جميرا.',
        introEn: 'Dubai Islands (formerly Deira Islands) is a Nakheel-developed group of islands off the Deira coast with beaches, marinas and resorts. It has become one of the busiest areas for new off-plan launches, aimed at buyers who want beachfront living below Palm Jumeirah prices.'
    },
    {
        slug: 'dubailand-residence-complex',
        short: 'Dubailand Residence Complex',
        ar: 'مجمع دبي لاند السكني',
        en: 'Dubailand Residence Complex',
        city: 'Dubai',
        match: ['Dubailand Residence Complex'],
        query: 'Dubailand Residence',
        introAr: 'مجمع دبي لاند السكني (DLRC) حيّ شقق متوسطة الارتفاع قرب شارع الإمارات وشارع الشيخ محمد بن زايد. من أقل مناطق دبي سعراً للشقق الجديدة، ويناسب من يبحث عن أول عقار استثماري بميزانية محدودة وخطط سداد طويلة.',
        introEn: 'Dubailand Residence Complex (DLRC) is a mid-rise apartment community near Emirates Road and Sheikh Mohammed Bin Zayed Road. It is one of the most affordable places in Dubai to buy a new apartment, suiting first-time investors on a tight budget who want long payment plans.'
    },
    {
        slug: 'arjan',
        short: 'Arjan',
        ar: 'أرجان',
        en: 'Arjan',
        city: 'Dubai',
        match: ['Arjan'],
        query: 'Arjan',
        introAr: 'أرجان حيّ سكني في دبي لاند بجوار حديقة دبي للزهور (ميراكل جاردن) وقريب من JVC وشارع أم سقيم. يغلب عليه أبراج الشقق متوسطة السعر، ويجذب المستثمرين الباحثين عن عائد إيجاري جيد بسعر دخول معقول.',
        introEn: 'Arjan is a residential community in Dubailand beside Dubai Miracle Garden, close to JVC and Umm Suqeim Road. It is dominated by mid-priced apartment towers and draws investors looking for solid rental yield at a reasonable entry price.'
    },
    {
        slug: 'al-furjan',
        short: 'Al Furjan',
        ar: 'الفرجان',
        en: 'Al Furjan',
        city: 'Dubai',
        match: ['Al Furjan'],
        query: 'Al Furjan',
        introAr: 'الفرجان مجتمع عائلي من نخيل بين شارع الشيخ زايد وشارع الشيخ محمد بن زايد، تخدمه محطة مترو على الخط الأحمر. يضم شققاً وتاون هاوس وفللاً، ويناسب العائلات التي تريد هدوء الضواحي مع وصول سريع إلى دبي مارينا وجبل علي.',
        introEn: 'Al Furjan is a Nakheel family community between Sheikh Zayed Road and Sheikh Mohammed Bin Zayed Road, served by a Red Line metro station. It offers apartments, townhouses and villas for families who want a quiet suburb with quick access to Dubai Marina and Jebel Ali.'
    },
    {
        slug: 'dubai-hills',
        short: 'Dubai Hills',
        ar: 'دبي هيلز إستيت',
        en: 'Dubai Hills Estate',
        city: 'Dubai',
        match: ['Dubai Hills'],
        query: 'Dubai Hills',
        introAr: 'دبي هيلز إستيت مجتمع متكامل من إعمار حول ملعب غولف ومنتزه مركزي، ويضم دبي هيلز مول ومدارس ومستشفى. من أكثر المناطق طلباً للسكن العائلي الراقي، وتتنوّع فيه المشاريع بين شقق قرب المول وفلل وتاون هاوس.',
        introEn: 'Dubai Hills Estate is an Emaar master community built around a golf course and central park, with Dubai Hills Mall, schools and a hospital. It is one of the most sought-after areas for upscale family living, with apartments near the mall alongside villas and townhouses.'
    },
    {
        slug: 'jvt',
        short: 'JVT',
        ar: 'مثلث قرية جميرا',
        en: 'Jumeirah Village Triangle',
        city: 'Dubai',
        match: ['JVT (Jumeirah Village Triangle)'],
        query: 'JVT',
        introAr: 'مثلث قرية جميرا (JVT) جار JVC الأهدأ، يغلب عليه الفلل والتاون هاوس مع عدد متزايد من مشاريع الشقق الجديدة. يناسب العائلات والمستثمرين الذين يريدون مساحات أكبر بأسعار أقل من المناطق الساحلية.',
        introEn: 'Jumeirah Village Triangle (JVT) is JVC\'s quieter neighbour, mostly villas and townhouses with a growing number of new apartment projects. It suits families and investors who want more space for less than the coastal communities.'
    },
    {
        slug: 'majan',
        short: 'Majan',
        ar: 'مجان',
        en: 'Majan',
        city: 'Dubai',
        match: ['Majan'],
        query: 'Majan',
        introAr: 'مجان حيّ في دبي لاند قرب شارع الإمارات وشارع الشيخ محمد بن زايد، يضم أبراج شقق جديدة بأسعار دخول منخفضة. يستهدف المستثمرين الباحثين عن وحدات صغيرة بخطط سداد ميسّرة وعائد إيجاري.',
        introEn: 'Majan is a Dubailand community near Emirates Road and Sheikh Mohammed Bin Zayed Road with new apartment towers at low entry prices. It targets investors after smaller units with easy payment plans and rental income.'
    },
    {
        slug: 'town-square',
        short: 'Town Square',
        ar: 'تاون سكوير',
        en: 'Town Square',
        city: 'Dubai',
        match: ['Town Square'],
        query: 'Town Square',
        introAr: 'تاون سكوير مجتمع عائلي من نشامى على شارع القدرة، يضم شققاً وتاون هاوس حول ساحة مركزية ومتنزهات ومسارات للمشي. يُعرف بأسعاره المناسبة للعائلات الشابة ومن يشتري أول منزل له في دبي.',
        introEn: 'Town Square is a Nshama family community on Al Qudra Road with apartments and townhouses around a central plaza, parks and walking trails. It is known for prices that suit young families and first-time home buyers in Dubai.'
    },
    {
        slug: 'palm-jumeirah',
        short: 'Palm Jumeirah',
        ar: 'نخلة جميرا',
        en: 'Palm Jumeirah',
        city: 'Dubai',
        match: ['Palm Jumeirah'],
        query: 'Palm Jumeirah',
        introAr: 'نخلة جميرا الجزيرة الأشهر في دبي، وتضم شققاً وفللاً على الشاطئ وفنادق فاخرة. المشاريع الجديدة فيها محدودة لأن الجزيرة شبه مكتملة، ولذلك تحافظ أسعارها على مستوى مرتفع وتستهدف شريحة الفخامة.',
        introEn: 'Palm Jumeirah is Dubai\'s best-known island, with beachfront apartments, villas and luxury hotels. New projects are scarce because the island is largely built out, which keeps prices high and squarely in the luxury segment.'
    },
    {
        slug: 'dubai-creek-harbour',
        short: 'Dubai Creek Harbour',
        ar: 'دبي كريك هاربر',
        en: 'Dubai Creek Harbour',
        city: 'Dubai',
        match: ['Dubai Creek Harbour'],
        query: 'Creek Harbour',
        introAr: 'دبي كريك هاربر مشروع إعمار على ضفاف خور دبي بجوار محمية رأس الخور، بإطلالات على أفق المدينة وبرج خليفة. يضم أبراج شقق حديثة ومراسي ومساحات خضراء، ويُعدّ من أبرز مشاريع إعمار على الخارطة.',
        introEn: 'Dubai Creek Harbour is Emaar\'s waterfront district on Dubai Creek beside the Ras Al Khor wildlife sanctuary, with views across the skyline to Burj Khalifa. It is made up of modern apartment towers, marinas and parkland, and is one of Emaar\'s flagship off-plan destinations.'
    },
    {
        slug: 'meydan',
        short: 'Meydan',
        ar: 'ميدان ومدينة محمد بن راشد',
        en: 'Meydan & MBR City',
        city: 'Dubai',
        match: ['Meydan (Nad Al Sheba  1)', 'Meydan', 'MBR District 11 (Meydan South)', 'Azizi Riviera at Meydan One'],
        query: 'Meydan',
        introAr: 'ميدان ومدينة محمد بن راشد منطقة واسعة قرب وسط مدينة دبي تضم مضمار ميدان للسباقات ومشاريع مثل عزيزي ريفييرا وصبحا وديستركت ون. تتنوّع بين الشقق بإطلالات على الكريستال لاجون والفلل الفاخرة، مع دقائق قليلة إلى داون تاون.',
        introEn: 'Meydan and Mohammed Bin Rashid City form a large area minutes from Downtown Dubai, home to Meydan Racecourse and projects such as Azizi Riviera, Sobha and District One. Stock ranges from lagoon-view apartments to luxury villas.'
    },
    {
        slug: 'dubai-production-city',
        short: 'Dubai Production City',
        ar: 'مدينة دبي للإنتاج',
        en: 'Dubai Production City',
        city: 'Dubai',
        match: ['Dubai Production City'],
        query: 'Production City',
        introAr: 'مدينة دبي للإنتاج (IMPZ سابقاً) حيّ شقق على شارع الشيخ محمد بن زايد قرب موتور سيتي وسبورتس سيتي. أسعارها من الأقل في دبي، وتناسب المستثمرين الباحثين عن وحدات صغيرة للإيجار.',
        introEn: 'Dubai Production City (formerly IMPZ) is an apartment district on Sheikh Mohammed Bin Zayed Road near Motor City and Sports City. Prices are among the lowest in Dubai, suiting investors after small units to rent out.'
    },
    {
        slug: 'al-jaddaf',
        short: 'Al Jaddaf',
        ar: 'الجداف',
        en: 'Al Jaddaf',
        city: 'Dubai',
        match: ['Al Jaddaf', 'Al Jaddaf Waterfront'],
        query: 'Jaddaf',
        introAr: 'الجداف منطقة على خور دبي بين ديرة ووسط المدينة، تخدمها محطة مترو على الخط الأخضر وقريبة من مدينة دبي الطبية ومطار دبي. تضم مشاريع شقق حديثة بإطلالات على الخور بأسعار أقل من المناطق المركزية المجاورة.',
        introEn: 'Al Jaddaf sits on Dubai Creek between Deira and Downtown, served by a Green Line metro station and close to Dubai Healthcare City and DXB airport. It offers new creek-view apartments for less than the central districts next door.'
    },
    {
        slug: 'dubai-maritime-city',
        short: 'Dubai Maritime City',
        ar: 'مدينة دبي الملاحية',
        en: 'Dubai Maritime City',
        city: 'Dubai',
        match: ['Maritime City'],
        query: 'Maritime City',
        introAr: 'مدينة دبي الملاحية شبه جزيرة بين ميناء راشد وجزر دبي، تتحوّل إلى حيّ سكني على البحر بأبراج شقق جديدة. تجمع بين إطلالات بحرية وقرب من بر دبي ووسط المدينة بأسعار أقل من دبي مارينا.',
        introEn: 'Dubai Maritime City is a peninsula between Mina Rashid and Dubai Islands that is turning into a seafront residential district of new apartment towers, with sea views and quick access to Bur Dubai and Downtown at prices below Dubai Marina.'
    },
    {
        slug: 'damac-hills',
        short: 'Damac Hills',
        ar: 'داماك هيلز',
        en: 'DAMAC Hills',
        city: 'Dubai',
        match: ['Damac Hills'],
        query: 'Damac Hills',
        introAr: 'داماك هيلز مجتمع من داماك حول نادي ترامب الدولي للغولف على شارع أم سقيم، يضم فللاً وتاون هاوس وشققاً. يناسب العائلات الباحثة عن مجتمع مسوّر بمرافق متكاملة ومساحات خضراء.',
        introEn: 'DAMAC Hills is a DAMAC community around the Trump International Golf Club on Umm Suqeim Road, with villas, townhouses and apartments. It suits families looking for a gated community with full amenities and green space.'
    },
    {
        slug: 'damac-hills-2',
        short: 'Damac Hills 2',
        ar: 'داماك هيلز 2',
        en: 'DAMAC Hills 2',
        city: 'Dubai',
        match: ['Damac Hills 2'],
        query: 'Damac Hills 2',
        introAr: 'داماك هيلز 2 (أكويا سابقاً) مجتمع فلل وتاون هاوس على شارع القدرة، يُعدّ من أرخص الخيارات لامتلاك فيلا أو تاون هاوس في دبي. يناسب العائلات والمستثمرين الباحثين عن مساحة أكبر بميزانية محدودة.',
        introEn: 'DAMAC Hills 2 (formerly Akoya) is a villa and townhouse community on Al Qudra Road and one of the cheapest ways to own a villa or townhouse in Dubai, suiting families and investors who want more space on a limited budget.'
    },
    {
        slug: 'damac-lagoons',
        short: 'Damac Lagoons',
        ar: 'داماك لاجونز',
        en: 'DAMAC Lagoons',
        city: 'Dubai',
        match: ['Damac Lagoons'],
        query: 'Damac Lagoons',
        introAr: 'داماك لاجونز مجتمع تاون هاوس وفلل مستوحى من مدن البحر المتوسط، حول بحيرات كريستالية وشواطئ صناعية قرب داماك هيلز. يستهدف العائلات الباحثة عن نمط حياة منتجعي داخل دبي.',
        introEn: 'DAMAC Lagoons is a Mediterranean-themed townhouse and villa community built around crystal lagoons and man-made beaches near DAMAC Hills, aimed at families who want resort-style living inside Dubai.'
    },
    {
        slug: 'damac-islands',
        short: 'Damac Islands',
        ar: 'داماك آيلاندز',
        en: 'DAMAC Islands',
        city: 'Dubai',
        match: ['Damac Islands', 'Damac Islands 2'],
        query: 'Damac Islands',
        introAr: 'داماك آيلاندز من أحدث مجتمعات داماك، تاون هاوس وفلل بطابع جزر استوائية حول بحيرات وشواطئ في دبي لاند. يُطرح على مراحل بخطط سداد طويلة ويجذب المستثمرين في المرحلة المبكّرة.',
        introEn: 'DAMAC Islands is one of DAMAC\'s newest communities: tropical island-themed townhouses and villas around lagoons and beaches in Dubailand, released in phases with long payment plans that attract early-stage investors.'
    },
    {
        slug: 'downtown-dubai',
        short: 'Downtown Dubai',
        ar: 'وسط مدينة دبي (داون تاون)',
        en: 'Downtown Dubai',
        city: 'Dubai',
        match: ['Downtown Dubai'],
        query: 'Downtown',
        introAr: 'وسط مدينة دبي (داون تاون) قلب المدينة حول برج خليفة ودبي مول ونافورة دبي. المشاريع الجديدة فيه قليلة ومرتفعة السعر، وتستهدف من يريد العنوان الأشهر في دبي وطلباً سياحياً على الإيجار القصير.',
        introEn: 'Downtown Dubai is the heart of the city around Burj Khalifa, Dubai Mall and the Dubai Fountain. New launches are few and premium-priced, aimed at buyers who want Dubai\'s most famous address and strong tourist short-let demand.'
    },
    {
        slug: 'emaar-south',
        short: 'Emaar South',
        ar: 'إعمار ساوث',
        en: 'Emaar South',
        city: 'Dubai',
        match: ['Emaar South'],
        query: 'Emaar South',
        introAr: 'إعمار ساوث مجتمع من إعمار داخل دبي الجنوب حول ملعب غولف وقرب مطار آل مكتوم، يضم شققاً وتاون هاوس بأسعار دخول أقل من مشاريع إعمار في وسط المدينة.',
        introEn: 'Emaar South is an Emaar community inside Dubai South, set around a golf course near Al Maktoum Airport, with apartments and townhouses at lower entry prices than Emaar\'s central projects.'
    },
    {
        slug: 'dubai-sports-city',
        short: 'Dubai Sports City',
        ar: 'مدينة دبي الرياضية',
        en: 'Dubai Sports City',
        city: 'Dubai',
        match: ['Dubai Sports City'],
        query: 'Sports City',
        introAr: 'مدينة دبي الرياضية حيّ سكني حول ملاعب وأكاديميات رياضية وملعب غولف قرب موتور سيتي، يضم شققاً بأسعار معقولة ويناسب المستثمرين الباحثين عن عائد إيجاري ثابت.',
        introEn: 'Dubai Sports City is a residential district around stadiums, sports academies and a golf course near Motor City, offering reasonably priced apartments for investors seeking steady rental income.'
    },
    {
        slug: 'jumeirah-garden-city',
        short: 'Jumeirah Garden City',
        ar: 'جميرا جاردن سيتي',
        en: 'Jumeirah Garden City',
        city: 'Dubai',
        match: ['Jumeirah Garden City'],
        query: 'Jumeirah Garden City',
        introAr: 'جميرا جاردن سيتي منطقة يُعاد تطويرها في قلب دبي بين شارع الشيخ زايد والسطوة، قرب داون تاون وسيتي ووك. تُطرح فيها مشاريع شقق جديدة بموقع مركزي نادر بالنسبة لأسعارها.',
        introEn: 'Jumeirah Garden City is a central district being redeveloped between Sheikh Zayed Road and Al Satwa, near Downtown and City Walk, where new apartment projects offer a rare central location for the price.'
    },
    {
        slug: 'dubai-marina',
        short: 'Dubai Marina',
        ar: 'دبي مارينا',
        en: 'Dubai Marina',
        city: 'Dubai',
        match: ['Dubai Marina'],
        query: 'Dubai Marina',
        introAr: 'دبي مارينا حيّ أبراج حول مرسى مائي بطول 3 كيلومترات قرب شاطئ JBR، ومن أعلى مناطق دبي طلباً على الإيجار السياحي والسنوي. المشاريع الجديدة فيه محدودة لأن الحيّ مكتمل تقريباً.',
        introEn: 'Dubai Marina is a high-rise district around a 3 km waterway next to JBR beach and one of Dubai\'s strongest areas for holiday and long-term rentals. New projects are limited because the district is largely complete.'
    },
    {
        slug: 'jlt',
        short: 'JLT',
        ar: 'أبراج بحيرات جميرا',
        en: 'Jumeirah Lake Towers',
        city: 'Dubai',
        match: ['JLT (Jumeirah Lake Towers)'],
        query: 'JLT',
        introAr: 'أبراج بحيرات جميرا (JLT) حيّ أبراج حول بحيرات صناعية مقابل دبي مارينا، تخدمه محطتا مترو. يجمع الشقق والمكاتب بأسعار أقل من المارينا مع طلب إيجاري ثابت.',
        introEn: 'Jumeirah Lake Towers (JLT) is a cluster of towers around man-made lakes opposite Dubai Marina, served by two metro stations, combining apartments and offices at prices below the Marina with steady rental demand.'
    },
    {
        slug: 'sobha-hartland',
        short: 'Sobha Hartland',
        ar: 'صبحا هارتلاند',
        en: 'Sobha Hartland',
        city: 'Dubai',
        match: ['Sobha Hartland', 'Sobha Hartland 2'],
        query: 'Sobha Hartland',
        introAr: 'صبحا هارتلاند مجتمع من صبحا العقارية في مدينة محمد بن راشد على قناة رأس الخور، يضم مدارس دولية ومساحات خضراء واسعة وشققاً وفللاً بتشطيبات عالية، ويُعرف بجودة البناء.',
        introEn: 'Sobha Hartland is a Sobha Realty community in Mohammed Bin Rashid City on the Ras Al Khor canal, with international schools, extensive green space and high-spec apartments and villas, known for its build quality.'
    },
    {
        slug: 'dubai-silicon-oasis',
        short: 'Dubai Silicon Oasis',
        ar: 'واحة دبي للسيليكون',
        en: 'Dubai Silicon Oasis',
        city: 'Dubai',
        match: ['Dubai Silicon Oasis'],
        query: 'Silicon Oasis',
        introAr: 'واحة دبي للسيليكون منطقة حرّة تقنية وسكنية على شارع دبي العين، تضم جامعات وشركات تقنية وشققاً بأسعار معقولة، وتناسب المستثمرين الباحثين عن مستأجرين من الطلاب والموظفين.',
        introEn: 'Dubai Silicon Oasis is a technology free zone and residential area on Dubai–Al Ain Road with universities, tech companies and reasonably priced apartments, suiting investors who rent to students and professionals.'
    },
    {
        slug: 'motor-city',
        short: 'Motor City',
        ar: 'موتور سيتي',
        en: 'Motor City',
        city: 'Dubai',
        match: ['Dubai Motor City'],
        query: 'Motor City',
        introAr: 'موتور سيتي مجتمع عائلي حول حلبة دبي أوتودروم على شارع الشيخ محمد بن زايد، يضم شققاً منخفضة الارتفاع وتاون هاوس ومساحات خضراء، ويُعرف بهدوئه وقربه من المدارس.',
        introEn: 'Motor City is a family community around the Dubai Autodrome on Sheikh Mohammed Bin Zayed Road with low-rise apartments, townhouses and green space, known for being quiet and close to schools.'
    },
    {
        slug: 'expo-city',
        short: 'Expo City',
        ar: 'مدينة إكسبو دبي',
        en: 'Expo City Dubai',
        city: 'Dubai',
        match: ['Dubai Expo City'],
        query: 'Expo City',
        introAr: 'مدينة إكسبو دبي هي موقع إكسبو 2020 بعد تحويله إلى مدينة مستدامة تضم مساكن ومكاتب ومعالم مثل ساحة الوصل، قرب مطار آل مكتوم ودبي الجنوب، وتخدمها محطة مترو.',
        introEn: 'Expo City Dubai is the former Expo 2020 site, converted into a sustainable city of homes, offices and landmarks such as Al Wasl Plaza, near Al Maktoum Airport and Dubai South and served by the metro.'
    },
    {
        slug: 'the-valley',
        short: 'The Valley',
        ar: 'ذا فالي',
        en: 'The Valley',
        city: 'Dubai',
        match: ['The Valley'],
        query: 'The Valley',
        introAr: 'ذا فالي مجتمع تاون هاوس وفلل من إعمار على شارع دبي العين، من أقل مشاريع إعمار سعراً للمنازل المستقلة، ويناسب العائلات الباحثة عن علامة إعمار بميزانية أقل.',
        introEn: 'The Valley is an Emaar townhouse and villa community on Dubai–Al Ain Road and one of Emaar\'s most affordable ways to own a house, suiting families who want the Emaar name on a smaller budget.'
    },
    {
        slug: 'international-city',
        short: 'International City',
        ar: 'المدينة العالمية',
        en: 'International City',
        city: 'Dubai',
        match: ['Dubai International City'],
        query: 'International City',
        introAr: 'المدينة العالمية حيّ سكني كبير قرب سوق التنين على شارع الإمارات، من أرخص مناطق دبي للشراء والإيجار، ويجذب المستثمرين الباحثين عن عائد إيجاري مرتفع بسعر دخول منخفض جداً.',
        introEn: 'International City is a large residential district by Dragon Mart off Emirates Road, one of the cheapest places in Dubai to buy and rent, drawing investors after high rental yields on a very low entry price.'
    },
    {
        slug: 'jebel-ali-village',
        short: 'Jebel Ali Village',
        ar: 'قرية جبل علي',
        en: 'Jebel Ali Village',
        city: 'Dubai',
        match: ['Jebel Ali Village'],
        query: 'Jebel Ali Village',
        introAr: 'قرية جبل علي حيّ فلل يُعاد تطويره قرب الفرجان ومحطة مترو جبل علي، يضم مشاريع فلل وتاون هاوس جديدة ويناسب العائلات الباحثة عن منازل بحدائق.',
        introEn: 'Jebel Ali Village is a villa neighbourhood being redeveloped near Al Furjan and the Jebel Ali metro station, with new villa and townhouse projects for families who want houses with gardens.'
    },
    {
        slug: 'mina-rashid',
        short: 'Mina Rashid',
        ar: 'ميناء راشد',
        en: 'Mina Rashid',
        city: 'Dubai',
        match: ['Mina Rashid'],
        query: 'Mina Rashid',
        introAr: 'ميناء راشد مرفأ تاريخي في بر دبي يُعاد تطويره إلى حيّ سكني على البحر يضم سفينة الملكة إليزابيث 2 ومرسى لليخوت، بمشاريع شقق من إعمار بإطلالات بحرية.',
        introEn: 'Mina Rashid is a historic Bur Dubai port being redeveloped into a seafront residential district, home to the Queen Elizabeth 2 and a superyacht marina, with Emaar apartment projects overlooking the sea.'
    },
    {
        slug: 'dubai-investments-park',
        short: 'Dubai Investments Park',
        ar: 'مجمع دبي للاستثمار',
        en: 'Dubai Investments Park',
        city: 'Dubai',
        match: ['Dubai Investments Park'],
        query: 'Investments Park',
        introAr: 'مجمع دبي للاستثمار (DIP) منطقة مختلطة صناعية وسكنية قرب إكسبو وجبل علي، تضم شققاً بأسعار منخفضة ويسكنها كثير من العاملين في المنطقة الصناعية والموانئ.',
        introEn: 'Dubai Investments Park (DIP) is a mixed industrial and residential zone near Expo City and Jebel Ali with low-priced apartments, popular with people who work in the nearby industrial and port areas.'
    },
    {
        slug: 'reem-island',
        short: 'Reem Island',
        ar: 'جزيرة الريم',
        en: 'Al Reem Island',
        city: 'Abu Dhabi',
        match: ['Reem Island', 'Al Reem Island'],
        query: 'Reem Island',
        introAr: 'جزيرة الريم في أبوظبي حيّ أبراج سكنية وتجارية بجوار جزيرة المارية (المركز المالي)، وهي منطقة استثمارية يحقّ للأجانب التملّك فيها. تضم شققاً بإطلالات بحرية ومرافق متكاملة.',
        introEn: 'Al Reem Island in Abu Dhabi is a district of residential and commercial towers next to Al Maryah Island, the financial centre. It is an investment zone open to foreign ownership, with sea-view apartments and full amenities.'
    },
    {
        slug: 'saadiyat-island',
        short: 'Saadiyat Island',
        ar: 'جزيرة السعديات',
        en: 'Saadiyat Island',
        city: 'Abu Dhabi',
        match: ['Al Saadiyat island'],
        query: 'Saadiyat',
        introAr: 'جزيرة السعديات في أبوظبي وجهة ثقافية وشاطئية تضم متحف اللوفر أبوظبي وشواطئ محمية، وتطرح فيها مشاريع فلل وشقق فاخرة لمن يبحث عن الفخامة الهادئة في العاصمة.',
        introEn: 'Saadiyat Island in Abu Dhabi is a cultural and beach destination home to Louvre Abu Dhabi and protected beaches, with luxury villa and apartment projects for buyers who want quiet luxury in the capital.'
    },
    {
        slug: 'yas-island',
        short: 'Yas Island',
        ar: 'جزيرة ياس',
        en: 'Yas Island',
        city: 'Abu Dhabi',
        match: ['Yas Island'],
        query: 'Yas Island',
        introAr: 'جزيرة ياس في أبوظبي وجهة ترفيهية تضم حلبة ياس مارينا وعالم فيراري ووارنر براذرز وياس مول، وتطرح مشاريع شقق وتاون هاوس وفلل بطلب إيجاري سياحي وعائلي.',
        introEn: 'Yas Island in Abu Dhabi is an entertainment destination with Yas Marina Circuit, Ferrari World, Warner Bros. World and Yas Mall, offering apartments, townhouses and villas with both tourist and family rental demand.'
    },
    {
        slug: 'al-marjan-island',
        short: 'Al Marjan Island',
        ar: 'جزيرة المرجان',
        en: 'Al Marjan Island',
        city: 'Ras Al Khaimah',
        match: ['Al Marjan Island'],
        query: 'Marjan',
        introAr: 'جزيرة المرجان في رأس الخيمة أرخبيل صناعي على البحر يشهد طفرة في المشاريع منذ الإعلان عن منتجع وين للألعاب والترفيه، وتطرح فيها شقق وفلل على الشاطئ بأسعار أقل من دبي.',
        introEn: 'Al Marjan Island in Ras Al Khaimah is a man-made seafront archipelago that has seen a wave of launches since the Wynn integrated resort was announced, offering beachfront apartments and villas below Dubai prices.'
    }
];

/** Fewer live projects than this and an area page would be thin. */
export const MIN_AREA_PROJECTS = 3;

export const areaBySlug = (slug) => AREAS.find((a) => a.slug === slug) || null;

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Does this raw project belong to the area? Exact feed-name match only. */
export function inArea(area, raw) {
    const d = norm(normaliseProject(raw).district);
    return !!d && area.match.some((m) => norm(m) === d);
}

/**
 * Everything the copy needs, from the projects in one area.
 *
 * "Live" means buyable with a published price — the only projects whose
 * numbers belong in a title. Sold-out and price-less projects are still
 * listed, but they never set the "from" price.
 */
export function areaStats(projects = []) {
    const all = projects.map(normaliseProject).filter((p) => p.id && p.title);
    const live = all.filter((p) => !p.soldOut && p.priceMin > 0).sort((a, b) => a.priceMin - b.priceMin);
    const prices = live.map((p) => p.priceMin);
    const median = prices.length ? prices[Math.floor((prices.length - 1) / 2)] : 0;

    const devCount = new Map();
    for (const p of live) if (p.developer) devCount.set(p.developer, (devCount.get(p.developer) || 0) + 1);
    const developers = [...devCount.entries()].sort((a, b) => b[1] - a[1]).map(([name, n]) => ({ name, n }));

    const now = new Date().getUTCFullYear();
    const years = live
        .map((p) => new Date(String(p.handover).slice(0, 10)).getUTCFullYear())
        .filter((y) => isFinite(y) && y >= now);

    return {
        total: all.length,
        liveCount: live.length,
        live,
        priceFrom: prices[0] || 0,
        priceMedian: median,
        // AED 2M is the property value behind the 10-year Golden Visa.
        goldenVisaCount: live.filter((p) => p.priceMin >= 2000000).length,
        developers,
        handoverFrom: years.length ? Math.min(...years) : null,
        handoverTo: years.length ? Math.max(...years) : null
    };
}

/** Is there enough live inventory to publish an indexable page? */
export const areaIsIndexable = (stats) => stats.liveCount >= MIN_AREA_PROJECTS;

/** Kept for callers that predate projectCount(). */
export const projectsAr = (n) => projectCount(n, 'ar');

/**
 * The area's name in a language. Only Arabic has its own; Russian, Chinese
 * and Urdu buyers search Dubai areas by their Latin names ("JVC", "Business
 * Bay"), so those pages use the English one.
 */
export const areaName = (area, lang) => (lang === 'ar' ? area.ar : area.en);

/** Curated intro paragraph, where one exists in this language. */
export const areaIntro = (area, lang) => (lang === 'ar' ? area.introAr : lang === 'en' ? area.introEn : '') || '';

/** Headings and labels of an area page, shared by React and the prerenderer. */
const AREA_LABELS = {
    ar: {
        h1: (a) => `مشاريع ${a.ar} على الخارطة`, faq: (a) => `أسئلة شائعة عن ${a.ar}`, list: (a, n) => `كل مشاريع ${a.ar} (${n})`,
        cta: (a) => `اطلب قائمة أسعار ${a.ar} من المطوّر مباشرة`, others: 'مناطق أخرى', all: 'كل المناطق',
        onSale: 'مشروع متاح', from: 'أقل سعر (درهم)', developers: 'مطوّر', handover: 'التسليم', notFound: 'المنطقة غير موجودة',
        link: (a) => `مشاريع ${a.ar} على الخارطة`
    },
    en: {
        h1: (a) => `${a.en} Off-Plan Projects`, faq: (a) => `${a.en} FAQ`, list: (a, n) => `All projects in ${a.en} (${n})`,
        cta: (a) => `Get the ${a.en} developer price list`, others: 'Other areas', all: 'All areas',
        onSale: 'projects on sale', from: 'from (AED)', developers: 'developers', handover: 'handover', notFound: 'Area not found',
        link: (a) => `${a.en} off-plan projects`
    },
    ru: {
        h1: (a) => `Новостройки в районе ${a.en}`, faq: (a) => `Вопросы о районе ${a.en}`, list: (a, n) => `Все проекты района ${a.en} (${n})`,
        cta: (a) => `Получить прайс застройщиков района ${a.en}`, others: 'Другие районы', all: 'Все районы',
        onSale: 'проектов в продаже', from: 'от (AED)', developers: 'застройщиков', handover: 'сдача', notFound: 'Район не найден',
        link: (a) => `Новостройки ${a.en}`
    },
    zh: {
        h1: (a) => `${a.en}期房项目`, faq: (a) => `${a.en}常见问题`, list: (a, n) => `${a.en}全部项目（${n}）`,
        cta: (a) => `获取${a.en}开发商价目表`, others: '其他区域', all: '全部区域',
        onSale: '在售项目', from: '起价（迪拉姆）', developers: '开发商', handover: '交付', notFound: '未找到该区域',
        link: (a) => `${a.en}期房项目`
    },
    ur: {
        h1: (a) => `${a.en} کے آف پلان پروجیکٹس`, faq: (a) => `${a.en} — عمومی سوالات`, list: (a, n) => `${a.en} کے تمام پروجیکٹس (${n})`,
        cta: (a) => `${a.en} کی ڈیولپر قیمتوں کی فہرست حاصل کریں`, others: 'دیگر علاقے', all: 'تمام علاقے',
        onSale: 'دستیاب پروجیکٹس', from: 'سے شروع (درہم)', developers: 'ڈیولپرز', handover: 'حوالگی', notFound: 'علاقہ نہیں ملا',
        link: (a) => `${a.en} آف پلان پروجیکٹس`
    }
};
export const areaLabels = (lang) => AREA_LABELS[lang] || AREA_LABELS.en;

/** Name for titles: the Latin short name people type, once. */
function titleName(a, lang) {
    if (lang === 'ar') return a.ar.includes(a.short) ? a.ar : `${a.ar} ${a.short}`;
    return a.short;
}

const yearsLabel = (s) => {
    if (!s.handoverFrom) return '';
    return s.handoverFrom === s.handoverTo ? String(s.handoverFrom) : `${s.handoverFrom}–${s.handoverTo}`;
};

/**
 * <title>. Arabic carries both scripts because most Arabic searchers still
 * type the area in Latin letters ("مشاريع JVC", "شقق بزنس باي").
 */
export function areaTitle(area, s, lang = 'ar') {
    const fromPrice = compactPrice(s.priceFrom, lang);
    const n = projectCount(s.liveCount, lang);
    const build = (name, from) => {
        switch (lang) {
            case 'ar': return `مشاريع ${name} على الخارطة | ${n}${from ? ` من ${from}` : ''}`;
            case 'ru': return `Новостройки ${name}: ${n}${from ? ` от ${from}` : ''}`;
            case 'zh': return `${name}期房项目｜${n}${from ? `，${from}起` : ''}`;
            case 'ur': return `${name} آف پلان پروجیکٹس | ${n}${from ? `، ${from} سے` : ''}`;
            default: return `${name} Off-Plan Projects | ${n}${from ? ` from ${from}` : ''}`;
        }
    };
    // 60 characters at most: the Arabic name with its Latin form, then the
    // Latin form alone (what people type), then without the starting price.
    const names = lang === 'ar' ? [titleName(area, lang), area.short, area.ar] : [titleName(area, lang)];
    for (const from of [fromPrice, null]) {
        for (const name of names) {
            const t = build(name, from);
            if (t.length <= 60) return t;
        }
    }
    return build(area.short, null);
}

/** Meta description: fact-first, unique per area, ≤158 chars on a clause. */
export function areaDescription(area, s, lang = 'ar') {
    const from = fullPrice(s.priceFrom, lang);
    const devs = listJoin(s.developers.slice(0, 2).map((d) => d.name), lang);
    const name = areaName(area, lang);
    const n = projectCount(s.liveCount, lang);
    const parts = {
        ar: [`${n} على الخارطة في ${name} بأسعار المطوّر مباشرة.`, from && `تبدأ من ${from}.`, devs && `من ${devs}.`, 'بدون عمولة على المشتري.'],
        en: [`Off-plan in ${name}: ${n} at direct developer prices.`, from && `From ${from}.`, devs && `By ${devs}.`, 'Zero buyer commission.'],
        ru: [`${n} на этапе строительства в районе ${name} по ценам застройщика.`, from && `От ${from}.`, devs && `Застройщики: ${devs}.`, 'Без комиссии для покупателя.'],
        zh: [`${name}在售期房：${n}，开发商直售价。`, from && `${from}起。`, devs && `开发商：${devs}。`, '买家零佣金。'],
        ur: [`${name} میں آف پلان: ${n}، براہِ راست ڈیولپر قیمت پر۔`, from && `${from} سے شروع۔`, devs && `ڈیولپرز: ${devs}۔`, 'خریدار سے کوئی کمیشن نہیں۔']
    }[lang] || [];
    const sep = lang === 'zh' ? '' : ' ';
    let out = '';
    for (const part of parts.filter(Boolean)) {
        const next = out ? `${out}${sep}${part}` : part;
        if (next.length > 158) break;
        out = next;
    }
    return out;
}

/** The direct-answer paragraph that opens the page. */
export function areaAnswer(area, s, lang = 'ar') {
    const from = fullPrice(s.priceFrom, lang);
    const median = fullPrice(s.priceMedian, lang);
    const cheapest = s.live[0];
    const devs = listJoin(s.developers.slice(0, 3).map((d) => d.name), lang);
    const years = yearsLabel(s);
    const name = areaName(area, lang);
    const city = cityName(area.city, lang);
    const n = projectCount(s.liveCount, lang);
    const lines = {
        ar: [
            `عدد المشاريع على الخارطة المتاحة للبيع حالياً في ${name} (${city}): ${n}، بأسعار المطوّر مباشرة.`,
            from && cheapest && `تبدأ الأسعار من ${from} في مشروع ${cheapest.title}، ويبلغ متوسط سعر البداية ${median}.`,
            devs && `أبرز المطوّرين في المنطقة ${devs}.`,
            years && `مواعيد التسليم المتوقّعة بين ${years}.`,
            'نبيع بسعر المطوّر الرسمي دون أي عمولة على المشتري.'
        ],
        en: [
            `${name} in ${city} currently has ${n} on sale off-plan, all at direct developer prices.`,
            from && cheapest && `Prices start from ${from} at ${cheapest.title}, with a median starting price of ${median}.`,
            devs && `Leading developers here include ${devs}.`,
            years && `Expected handovers run ${years}.`,
            'We sell at the official developer price with zero buyer commission.'
        ],
        ru: [
            `В районе ${name} (${city}) сейчас в продаже ${n} на этапе строительства по ценам застройщика.`,
            from && cheapest && `Цены начинаются от ${from} в проекте ${cheapest.title}; медианная стартовая цена — ${median}.`,
            devs && `Основные застройщики района: ${devs}.`,
            years && `Ожидаемые сроки сдачи: ${years}.`,
            'Мы продаём по официальной цене застройщика без комиссии для покупателя.'
        ],
        zh: [
            `${city}${name}目前有${n}在售（期房），均为开发商直售价。`,
            from && cheapest && `价格从${cheapest.title}的${from}起，起价中位数为${median}。`,
            devs && `区域主要开发商：${devs}。`,
            years && `预计交付时间为${years}年。`,
            '我们按开发商官方价格销售，买家零佣金。'
        ],
        ur: [
            `${name} (${city}) میں اس وقت آف پلان ${n} فروخت کے لیے دستیاب ہیں، براہِ راست ڈیولپر قیمت پر۔`,
            from && cheapest && `قیمتیں ${cheapest.title} میں ${from} سے شروع ہوتی ہیں، اور ابتدائی قیمت کا درمیانہ ${median} ہے۔`,
            devs && `علاقے کے نمایاں ڈیولپرز: ${devs}۔`,
            years && `متوقع حوالگی: ${years}۔`,
            'ہم ڈیولپر کی سرکاری قیمت پر، خریدار سے کوئی کمیشن لیے بغیر فروخت کرتے ہیں۔'
        ]
    }[lang] || [];
    return lines.filter(Boolean).join(lang === 'zh' ? '' : ' ');
}

/**
 * Questions people actually type about an area, answered from the live data.
 * Each answer is specific to the area, so the pages never share an FAQ.
 */
export function areaFaq(area, s, lang = 'ar') {
    const from = fullPrice(s.priceFrom, lang);
    const median = fullPrice(s.priceMedian, lang);
    const cheap = s.live.slice(0, 3);
    const years = yearsLabel(s);
    const name = areaName(area, lang);
    const n = projectCount(s.liveCount, lang);
    const gv = projectCount(s.goldenVisaCount, lang);
    const cheapList = (sep, fmt) => cheap.map((p) => fmt(p.title, fullPrice(p.priceMin, lang))).join(sep);
    const devList = (sep) => s.developers.slice(0, 5).map((d) => `${d.name} (${d.n})`).join(sep);

    const Q = {
        ar: {
            price: [`كم أسعار العقارات على الخارطة في ${name}؟`, `تبدأ أسعار المشاريع على الخارطة في ${name} من ${from}، ويبلغ متوسط سعر البداية ${median} (عدد المشاريع المتاحة: ${n}). يختلف السعر حسب نوع الوحدة وعدد الغرف والطابق والإطلالة، والأسعار المعروضة هي أسعار المطوّر الرسمية.`],
            cheap: [`ما أرخص المشاريع على الخارطة في ${name}؟`, `أقل المشاريع سعراً حالياً في ${name}: ${cheapList('، ', (t, p) => `${t} من ${p}`)}. تتغيّر الأسعار مع بيع الوحدات، لذلك نؤكّد السعر والتوفّر من المطوّر قبل الحجز.`],
            devs: [`من أبرز المطوّرين في ${name}؟`, `أكثر المطوّرين طرحاً للمشاريع في ${name} حالياً: ${devList('، ')}.`],
            handover: [`متى موعد تسليم المشاريع في ${name}؟`, `تتراوح مواعيد التسليم المتوقّعة للمشاريع المتاحة في ${name} بين ${years}. المشاريع ذات التسليم الأقرب تناسب من يريد السكن أو التأجير سريعاً، والأبعد تمنح عادةً خطط سداد أطول.`],
            gv: [`هل يمكن الحصول على الإقامة الذهبية بشراء عقار في ${name}؟`, s.goldenVisaCount
                ? `نعم، تُمنح الإقامة الذهبية لعشر سنوات لمالك عقار بقيمة 2 مليون درهم فأكثر، ويشمل ذلك العقارات على الخارطة. عدد المشاريع في ${name} التي يبدأ سعرها من 2 مليون درهم أو أكثر: ${gv}.`
                : `تُمنح الإقامة الذهبية لعشر سنوات لمالك عقار بقيمة 2 مليون درهم فأكثر. معظم مشاريع ${name} تبدأ بأقل من ذلك، لكن يمكن الوصول إلى الحدّ بوحدة أكبر أو بأكثر من وحدة.`]
        },
        en: {
            price: [`How much do off-plan properties cost in ${name}?`, `Off-plan projects in ${name} start from ${from}, with a median starting price of ${median} across ${n} on sale. Prices vary with unit type, bedrooms, floor and view; the prices shown are official developer prices.`],
            cheap: [`What are the cheapest off-plan projects in ${name}?`, `The lowest-priced projects in ${name} right now are ${cheapList(', ', (t, p) => `${t} from ${p}`)}. Prices move as units sell, so we confirm price and availability with the developer before you reserve.`],
            devs: [`Which developers are building in ${name}?`, `The developers with the most projects in ${name} right now: ${devList(', ')}.`],
            handover: [`When do projects in ${name} hand over?`, `Expected handovers for projects on sale in ${name} run ${years}. Earlier handovers suit buyers who want to move in or rent out soon; later ones usually come with longer payment plans.`],
            gv: [`Can I get a Golden Visa by buying in ${name}?`, s.goldenVisaCount
                ? `Yes. The 10-year Golden Visa is available to owners of property worth AED 2 million or more, including off-plan. ${gv} in ${name} start at AED 2 million or above.`
                : `The 10-year Golden Visa requires property worth AED 2 million or more. Most projects in ${name} start below that, but a larger unit or more than one unit can reach the threshold.`]
        },
        ru: {
            price: [`Сколько стоят новостройки в районе ${name}?`, `Новостройки в районе ${name} стоят от ${from}; медианная стартовая цена — ${median} (в продаже: ${n}). Цена зависит от типа юнита, числа спален, этажа и вида; указаны официальные цены застройщиков.`],
            cheap: [`Какие самые доступные проекты в районе ${name}?`, `Самые доступные проекты в районе ${name} сейчас: ${cheapList('; ', (t, p) => `${t} — от ${p}`)}. Цены меняются по мере продаж, поэтому перед бронированием мы подтверждаем цену и наличие у застройщика.`],
            devs: [`Какие застройщики строят в районе ${name}?`, `Больше всего проектов в районе ${name} сейчас у застройщиков: ${devList(', ')}.`],
            handover: [`Когда сдаются проекты в районе ${name}?`, `Ожидаемые сроки сдачи проектов в районе ${name}: ${years}. Ранняя сдача подходит тем, кто хочет скорее заселиться или сдавать в аренду; поздняя обычно даёт более длинный план оплаты.`],
            gv: [`Можно ли получить золотую визу, купив недвижимость в районе ${name}?`, s.goldenVisaCount
                ? `Да. Золотая виза на 10 лет доступна владельцам недвижимости стоимостью от 2 млн AED, включая строящуюся. В районе ${name} от 2 млн AED начинается ${gv}.`
                : `Для золотой визы на 10 лет нужна недвижимость стоимостью от 2 млн AED. Большинство проектов в районе ${name} дешевле, но порога можно достичь более крупным юнитом или несколькими юнитами.`]
        },
        zh: {
            price: [`${name}的期房价格是多少？`, `${name}的期房项目${from}起，起价中位数为${median}（在售${n}）。价格因户型、卧室数、楼层和景观而异，所示均为开发商官方价格。`],
            cheap: [`${name}最便宜的期房项目有哪些？`, `${name}目前价格最低的项目：${cheapList('；', (t, p) => `${t}，${p}起`)}。价格会随销售变化，预订前我们会向开发商确认价格和房源。`],
            devs: [`哪些开发商在${name}开发项目？`, `目前在${name}项目最多的开发商：${devList('、')}。`],
            handover: [`${name}的项目何时交付？`, `${name}在售项目的预计交付时间为${years}年。较早交付适合希望尽快入住或出租的买家，较晚交付通常付款计划更长。`],
            gv: [`在${name}购房能获得黄金签证吗？`, s.goldenVisaCount
                ? `可以。拥有价值200万迪拉姆及以上房产（含期房）的业主可申请10年黄金签证。${name}起价在200万迪拉姆及以上的项目有${gv}。`
                : `10年黄金签证要求房产价值200万迪拉姆及以上。${name}多数项目起价低于此标准，但可通过购买更大户型或多套房产达到门槛。`]
        },
        ur: {
            price: [`${name} میں آف پلان جائیداد کی قیمت کیا ہے؟`, `${name} میں آف پلان پروجیکٹس ${from} سے شروع ہوتے ہیں، اور ابتدائی قیمت کا درمیانہ ${median} ہے (دستیاب: ${n})۔ قیمت یونٹ کی قسم، بیڈرومز، منزل اور منظر کے مطابق بدلتی ہے؛ دکھائی گئی قیمتیں ڈیولپر کی سرکاری قیمتیں ہیں۔`],
            cheap: [`${name} میں سب سے سستے آف پلان پروجیکٹس کون سے ہیں؟`, `${name} میں اس وقت سب سے کم قیمت پروجیکٹس: ${cheapList('، ', (t, p) => `${t} — ${p} سے`)}۔ قیمتیں فروخت کے ساتھ بدلتی ہیں، اس لیے بکنگ سے پہلے ہم ڈیولپر سے قیمت اور دستیابی کی تصدیق کرتے ہیں۔`],
            devs: [`${name} میں کون سے ڈیولپرز تعمیر کر رہے ہیں؟`, `${name} میں اس وقت سب سے زیادہ پروجیکٹس والے ڈیولپرز: ${devList('، ')}۔`],
            handover: [`${name} میں پروجیکٹس کی حوالگی کب ہے؟`, `${name} میں دستیاب پروجیکٹس کی متوقع حوالگی ${years} کے درمیان ہے۔ جلد حوالگی جلد رہائش یا کرایہ چاہنے والوں کے لیے، اور دیر والی عموماً لمبے ادائیگی پلان کے ساتھ آتی ہے۔`],
            gv: [`کیا ${name} میں جائیداد خرید کر گولڈن ویزا مل سکتا ہے؟`, s.goldenVisaCount
                ? `جی ہاں۔ 20 لاکھ درہم یا اس سے زیادہ مالیت کی جائیداد (آف پلان سمیت) کے مالک کو 10 سالہ گولڈن ویزا مل سکتا ہے۔ ${name} میں 20 لاکھ درہم یا اس سے زیادہ سے شروع ہونے والے پروجیکٹس: ${gv}۔`
                : `10 سالہ گولڈن ویزا کے لیے 20 لاکھ درہم یا اس سے زیادہ مالیت کی جائیداد درکار ہے۔ ${name} کے زیادہ تر پروجیکٹس اس سے کم سے شروع ہوتے ہیں، لیکن بڑے یونٹ یا ایک سے زیادہ یونٹس سے یہ حد پوری ہو سکتی ہے۔`]
        }
    }[lang] || {};

    const out = [];
    if (from && Q.price) out.push(Q.price);
    if (cheap.length >= 2 && Q.cheap) out.push(Q.cheap);
    if (s.developers.length && Q.devs) out.push(Q.devs);
    if (years && Q.handover) out.push(Q.handover);
    if (Q.gv) out.push(Q.gv);
    return out.map(([q, a]) => ({ q, a }));
}

/**
 * Every area that gets a page: the curated ones above, plus one per feed
 * district they do not cover. An uncovered district is otherwise reachable
 * only through its projects' related links — the brief is that no project
 * sits more than three clicks from the homepage, and home → areas → area →
 * project is that path.
 *
 * Generated areas carry the feed's own name (cleaned) in every language and
 * no curated intro; their answer paragraph and FAQ still come from their own
 * numbers, so the page is not a template.
 */
export function buildAreaList(rows) {
    const covered = new Set(AREAS.flatMap((a) => a.match.map(norm)));
    const taken = new Set(AREAS.map((a) => a.slug));
    const byDistrict = new Map();
    for (const raw of rows) {
        const p = normaliseProject(raw);
        if (!p.district || covered.has(norm(p.district))) continue;
        if (!byDistrict.has(norm(p.district))) byDistrict.set(norm(p.district), { name: p.district, city: p.city });
    }
    const generated = [];
    for (const { name, city } of byDistrict.values()) {
        // Some feed names are all lower case ("al satwa"); a heading should not be.
        const raw = districtLabel(name);
        const label = /[A-Z]/.test(raw) ? raw : raw.replace(/\b[a-z]/g, (c) => c.toUpperCase());
        let slug = slugify(label);
        for (let i = 2; taken.has(slug); i++) slug = `${slugify(label)}-${i}`;
        taken.add(slug);
        generated.push({ slug, short: label, ar: label, en: label, city: city || 'Dubai', match: [name], query: name, generated: true });
    }
    return [...AREAS, ...generated.sort((a, b) => a.en.localeCompare(b.en))];
}

/** district → area, over a full area list (curated + generated). */
export function areaIndex(list) {
    const map = new Map();
    for (const a of list) for (const m of a.match) map.set(norm(m), a);
    return (district) => map.get(norm(district)) || null;
}

/** The area page a feed district belongs to, if it has one. */
export function areaForDistrict(district) {
    const d = norm(district);
    if (!d) return null;
    return AREAS.find((a) => a.match.some((m) => norm(m) === d)) || null;
}

/** Breadcrumb/link target for a district: its area page, else the filtered list. */
export const districtUrl = (district) => {
    const area = areaForDistrict(district);
    return area ? `/area/${area.slug}` : `/projects?district=${encodeURIComponent(district)}`;
};
